import {
  baseQuery,
  CREATE_TENANT,
  GET_ALL_TENANTS,
  ENABLE_DISABLE_TENANT,
  DELETE_TENANT,
} from "@/graphql";
import { RootState } from "@/store";
import { createApi } from "@reduxjs/toolkit/query/react";
import { setSelectedTenant, setTenantManagement } from "./tenant-slice";
import { signedUrlApi } from "../signed-url/signed-url-api";

interface DeleteTenantResponse {
  deleteTenant: {
    data: string;
  };
}

interface TenantManagementResponse {
  data: ITenant[];
  meta: {
    countDocs: number;
    pageNo: number;
    pageSize: number;
  };
}
interface AddTenantResponse {
  data: boolean;
}
interface EnableDisableTenantResponse {
  data: boolean;
}


type ICreateTenantInput = Pick<
  ITenant,
  "companyName" | "companyEmail" | "businessAddress" | "companyLogo" | "tenantType"
>;
type ICreateTenantWithPages = ICreateTenantInput & { allowedPages?: string[] };

// Define the API
export const tenantManagementApi = createApi({
  reducerPath: "api/tenant",
  tagTypes: ["tenantManagement"],
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getAllTenants: builder.query<
    TenantManagementResponse,
      {
        pageNo: number;
        pageSize: number;
        searchKeyword?: string;
      }
    >({
      query: (variables) => ({
          document: GET_ALL_TENANTS,
          variables: { ...variables },
      }),
      providesTags: ["tenantManagement"],
      transformResponse: (response: {
        getAllTenants: TenantManagementResponse;
      }) => response?.getAllTenants,
      onQueryStarted: async (_, { getState, dispatch, queryFulfilled }) => {
        const state = getState() as RootState;

        const { selectedTenant } = state.tenantManagement;
        const { data } = (await queryFulfilled).data;

        const TenantsWithProfileImage = await Promise.all(
          data.map(async (tenant) => {
            if (tenant.companyLogo) {
              const { signedUrl } = await dispatch(
                signedUrlApi.endpoints.getAccessSignedUrl.initiate({
                  fileName: tenant.companyLogo,
                })
              ).unwrap();

              return { ...tenant, companyLogo: signedUrl };
            }
            return tenant;
          })
        );


        dispatch(setTenantManagement(TenantsWithProfileImage));

        // Prefer persisted selection (by ID) if available
        let hydrated = false;
        try {
          const storedTenantId = localStorage.getItem("selectedTenantId");
          if (storedTenantId) {
            const match = TenantsWithProfileImage.find((t) => t._id === storedTenantId);
            if (match && (!selectedTenant || selectedTenant._id !== storedTenantId)) {
              dispatch(setSelectedTenant(match));
              hydrated = true;
            }
          }
        } catch {}

        // Fallback: if still nothing selected, default to first item
        if (!hydrated && !selectedTenant && TenantsWithProfileImage.length > 0) {
          dispatch(setSelectedTenant(TenantsWithProfileImage[0]));
        }

        const updatedSelectedTenant = TenantsWithProfileImage.find(
          (tenant) => tenant._id === selectedTenant?._id
        );

        if (
          TenantsWithProfileImage.length > 0 &&
          updatedSelectedTenant &&
          selectedTenant?.companyLogo !== updatedSelectedTenant?.companyLogo
        ) {
          dispatch(setSelectedTenant(updatedSelectedTenant));
        }
      },
    }),
    // Define the createUser mutation
    createTenant: builder.mutation<
      AddTenantResponse,
      { tenantData: ICreateTenantWithPages }
    >({
      query: ({ tenantData }) => {
        return {
          document: CREATE_TENANT,
          variables: { tenantData },
        };
      },
      invalidatesTags: ["tenantManagement"],
    }),
    enableDisableTenant: builder.mutation<
      EnableDisableTenantResponse,
      { tenantId: string; isRestricted: boolean }
    >({
      query: ({ tenantId, isRestricted }) => ({
        document: ENABLE_DISABLE_TENANT,
        variables: { tenantId, isRestricted },
      }),
      invalidatesTags: ["tenantManagement"],
    }),
    deleteTenant: builder.mutation<
      DeleteTenantResponse,
      { tenantId: string }
    >({
      query: ({ tenantId }) => ({
        document: DELETE_TENANT,
        variables: { tenantId },
      }),
      invalidatesTags: ["tenantManagement"],
    }),

  }),
});

// Extract hook
export const {
  useGetAllTenantsQuery,
  useCreateTenantMutation,
  useEnableDisableTenantMutation,
  useDeleteTenantMutation,
} = tenantManagementApi;
